import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { ToastProvider } from '@/components/ui/toast';
import { AppShell } from '@/layouts/AppShell';
import { AuthProvider } from '@/lib/auth';
import { CompanyProvider } from '@/lib/company';
import { useAuth } from '@/lib/auth-context';
import { FunnelDetailsPage } from '@/pages/FunnelDetailsPage';
import { FunnelPage } from '@/pages/FunnelPage';
import { HomePage } from '@/pages/HomePage';
import { LeadDetailPage } from '@/pages/LeadDetailPage';
import { LoginPage } from '@/pages/LoginPage';
import { MyDayPage } from '@/pages/MyDayPage';
import { NewLeadPage } from '@/pages/NewLeadPage';
import { LeadsRedirect, PipelineSection } from '@/pages/PipelineSection';
import { AttentionPage } from '@/pages/AttentionPage';
import { AdminPage } from '@/pages/admin/AdminPage';
import { CommercialPage } from '@/pages/commercial/CommercialPage';
import { TeamPage } from '@/pages/TeamPage';
import { IngestPage } from '@/pages/ingest/IngestPage';
import { DailyReportPage } from '@/pages/reports/DailyReportPage';
import { LeadQualityPage } from '@/pages/reports/LeadQualityPage';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000, refetchOnWindowFocus: false } },
});

function RequireAuth() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="text-muted-foreground flex min-h-screen items-center justify-center text-sm">
        Cargando sesión…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <Outlet />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <CompanyProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route element={<RequireAuth />}>
                  <Route element={<AppShell />}>
                    <Route index element={<MyDayPage />} />
                    <Route path="leads" element={<LeadsRedirect />} />
                    <Route path="leads/new" element={<NewLeadPage />} />
                    <Route path="leads/:id" element={<LeadDetailPage />} />
                    <Route path="pipeline" element={<PipelineSection />} />
                    <Route path="atencion" element={<AttentionPage />} />
                    <Route path="embudo" element={<FunnelPage />} />
                    <Route path="embudo/detalle" element={<FunnelDetailsPage />} />
                    <Route path="equipo" element={<TeamPage />} />
                    <Route path="ingesta" element={<IngestPage />} />
                    <Route path="comercial" element={<CommercialPage />} />
                    <Route path="admin" element={<AdminPage />} />
                    <Route path="reportes/calidad" element={<LeadQualityPage />} />
                    <Route path="reportes/gestion-diaria" element={<DailyReportPage />} />
                    <Route path="resumen" element={<HomePage />} />
                  </Route>
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
          </CompanyProvider>
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
